// Static copy and assets for the junior colouring-in competition (school
// holidays, launched by club email on 26 Sep 2026). Single source of truth for
// the landing-page card and the /colouring-competition page.
//
// House style (matches content/groperStory.ts): Australian English, no em
// dashes anywhere in this copy.

export const COLOURING_PATH = '/colouring-competition';

/**
 * Last day the landing-page card is shown (inclusive, club local date).
 * Entries are handed in at the next sign-on. The October competition was not
 * yet scheduled when this shipped; the club's recent competitions were on the
 * third Sunday (16 Aug, 20 Sep), so the October one is expected Sun 18 Oct
 * 2026. Change this date if the competition is set for a different day.
 */
export const COLOURING_CARD_LAST_DAY = '2026-10-18';

/** True while the landing-page card should show. The page stays reachable. */
export function isColouringCompOpen(now: Date = new Date()): boolean {
  // Compare as club-local (Australia/Sydney) calendar dates, not UTC instants,
  // so the card disappears at local midnight after the last day.
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  return today <= COLOURING_CARD_LAST_DAY;
}

export interface ColouringSheet {
  id: string;
  title: string;
  alt: string;
  pdf: string;
  /** 1200px JPG fallback */
  jpg: string;
  /** WebP sources for srcset */
  webp640: string;
  webp1200: string;
  width: number;
  height: number;
}

export const COLOURING_SHEETS: readonly ColouringSheet[] = [
  {
    id: 'sheet-1',
    title: 'Sheet 1: The big jump',
    alt: 'Colouring sheet: a spotted fish leaping out of the river towards a lure, with a fishing rod, tackle box, bucket, boat and sun',
    pdf: '/colouring/colouring-sheet-1.pdf',
    jpg: '/colouring/colouring-sheet-1.jpg',
    webp640: '/colouring/colouring-sheet-1-640.webp',
    webp1200: '/colouring/colouring-sheet-1.webp',
    width: 1200,
    height: 800,
  },
  {
    id: 'sheet-2',
    title: 'Sheet 2: Groper on the Tweed',
    alt: 'Colouring sheet: a large groper swimming under the Tweed River, with a boat, the bridge, mountains and a Tweed River sign',
    pdf: '/colouring/colouring-sheet-2.pdf',
    jpg: '/colouring/colouring-sheet-2.jpg',
    webp640: '/colouring/colouring-sheet-2-640.webp',
    webp1200: '/colouring/colouring-sheet-2.webp',
    width: 1200,
    height: 829,
  },
] as const;

export const COLOURING_CARD = {
  pill: 'JUNIORS',
  tag: 'SCHOOL HOLIDAYS',
  headline: 'Junior Colouring-In Competition',
  teaser:
    'Two fishing colouring sheets to keep our junior artists busy over the school holidays. Print one (or both), colour it in and bring your masterpiece to the next sign-on to be in the running for a prize.',
  cta: 'Get the colouring sheets →',
} as const;

export const COLOURING_PAGE = {
  heading: 'Junior Colouring-In Competition',
  intro:
    'For all of our junior artists, we have launched our colouring-in competition to keep you busy over the school holidays. Bring your masterpiece to the next sign-on and we will see who gets a prize.',
  howToEnterHeading: 'How to enter',
  steps: [
    'Download and print a colouring sheet below (A4 landscape).',
    'Colour it in.',
    'Bring your masterpiece to the next sign-on.',
  ],
  closingNote: 'Entries are judged at the next sign-on, and we will see who gets a prize.',
  downloadLabel: 'Download PDF',
} as const;
